// Admin → Challenge Loaders: the library of short looping MP4s shown while a
// challenge is prepared. Videos live in GCS; the metadata here (categories,
// General fallback, weight, active, seasonal window) is authoritative and is
// what runtime selection uses.
//
// "Suggest Categories with AI" is optional and only runs when clicked. It
// fills a suggestion panel; nothing reaches the form until "Apply
// Suggestions", and nothing is saved until "Save".
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import adminApi from '../../services/adminApi';
import Modal from '../../components/Modal/Modal';
import styles from './AdminChallengeLoaders.module.css';

const AI_FAILED = 'We couldn’t analyze this video. You can choose categories manually.';
const FALLBACK_META = {
    upload: { maxFileSizeBytes: 25 * 1024 * 1024, maxDurationSeconds: 20, acceptedExtensions: ['mp4', 'm4v', 'mov', 'webm'], recommended: { minDurationSeconds: 4, maxDurationSeconds: 6, minDimension: 512, maxDimension: 720 } },
    weight: { min: 1, max: 100 },
    selection: { generalShare: 0.2 },
    ai: { enabled: false, model: null, frameCount: 4 },
};
const EMPTY_FORM = {
    name: '', description: '', categoryIds: [], is_general: false, is_active: true, weight: '1', sort_order: '',
    available_from: '', available_until: '',
};

const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
const formatSeconds = (ms) => (ms ? `${(ms / 1000).toFixed(1)}s` : '—');
const formatMb = (bytes) => (bytes ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : '—');
const pct = (c) => (c == null ? '' : ` ${Math.round(c * 100)}%`);

function formFromLoader(loader) {
    return {
        name: loader.name,
        description: loader.description || '',
        categoryIds: loader.categoryIds || [],
        is_general: loader.isGeneral,
        is_active: loader.isActive,
        weight: String(loader.weight),
        sort_order: String(loader.sortOrder),
        available_from: toDateInput(loader.availableFrom),
        available_until: toDateInput(loader.availableUntil),
    };
}

// ── AI suggestion panel ──────────────────────────────────────────────────────

/** Editable copy of an AI suggestion; the admin trims it before applying. */
function suggestionDraft(suggestion) {
    return {
        name: suggestion.suggestedName || '',
        description: suggestion.description || '',
        isGeneral: Boolean(suggestion.isGeneral),
        categoryIds: (suggestion.categories || []).map((c) => c.id),
    };
}

function AiSuggestions({ suggestion, categories, onApply, onDiscard }) {
    const [draft, setDraft] = useState(() => suggestionDraft(suggestion));
    const confidence = Object.fromEntries((suggestion.categories || []).map((c) => [c.id, c.confidence]));
    // Suggested categories first, then everything else so the admin can add tags.
    const ordered = [
        ...categories.filter((c) => confidence[c.id] !== undefined).sort((a, b) => (confidence[b.id] ?? 0) - (confidence[a.id] ?? 0)),
        ...categories.filter((c) => confidence[c.id] === undefined),
    ];
    const toggle = (id) => setDraft((d) => ({
        ...d, categoryIds: d.categoryIds.includes(id) ? d.categoryIds.filter((x) => x !== id) : [...d.categoryIds, id],
    }));

    return (
        <section className={styles.aiPanel} aria-label="AI Suggestions">
            <h3 className={styles.aiTitle}>AI Suggestions</h3>
            <label>Name
                <input value={draft.name} maxLength={80} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} aria-label="Suggested name" />
            </label>
            <fieldset className={styles.chipFieldset}>
                <legend>Categories</legend>
                <div className={styles.checkGrid}>
                    {ordered.map((c) => (
                        <label key={c.id} className={styles.checkItem}>
                            <input type="checkbox" checked={draft.categoryIds.includes(c.id)} onChange={() => toggle(c.id)} aria-label={`Suggested ${c.name}`} />
                            {c.name}
                            {confidence[c.id] !== undefined && <span className={styles.confidence}>{pct(confidence[c.id])}</span>}
                        </label>
                    ))}
                    <label className={styles.checkItem}>
                        <input type="checkbox" checked={draft.isGeneral} onChange={(e) => setDraft((d) => ({ ...d, isGeneral: e.target.checked }))} aria-label="Suggested General" />
                        General
                    </label>
                </div>
            </fieldset>
            {suggestion.ignoredCategories?.length > 0 && (
                <small className={styles.hint}>Ignored (not one of your categories): {suggestion.ignoredCategories.join(', ')}</small>
            )}
            <label>Description
                <textarea rows={2} maxLength={500} value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} aria-label="Suggested description" />
            </label>
            <div className={styles.formActions}>
                <button type="button" className={styles.secondaryBtn} onClick={onDiscard}>Discard</button>
                <button type="button" className={styles.primaryBtn} onClick={() => onApply(draft)}>Apply Suggestions</button>
            </div>
        </section>
    );
}

// ── Create / edit form ───────────────────────────────────────────────────────

function LoaderForm({ loader, categories, meta, onCancel, onSaved }) {
    const isEdit = Boolean(loader);
    const [form, setForm] = useState(() => (loader ? formFromLoader(loader) : EMPTY_FORM));
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(loader?.videoUrl || null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [ai, setAi] = useState({ status: 'idle', suggestion: null, token: null, error: null });

    useEffect(() => () => { if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview); }, [preview]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    const toggleCategory = (id) => setForm((f) => ({
        ...f, categoryIds: f.categoryIds.includes(id) ? f.categoryIds.filter((x) => x !== id) : [...f.categoryIds, id],
    }));

    const handleFile = (e) => {
        const picked = e.target.files?.[0];
        e.target.value = '';
        if (!picked) return;
        const ext = (picked.name.split('.').pop() || '').toLowerCase();
        if (!meta.upload.acceptedExtensions.includes(ext)) return setError('Choose an MP4 video.');
        if (picked.size > meta.upload.maxFileSizeBytes) return setError(`Video must be ${formatMb(meta.upload.maxFileSizeBytes)} or smaller.`);
        setError(null);
        setFile(picked);
        setPreview(URL.createObjectURL(picked));
        // A suggestion belongs to the video it was made from.
        setAi({ status: 'idle', suggestion: null, token: null, error: null });
    };

    const canAnalyze = meta.ai.enabled && (file || isEdit);
    const analyze = async () => {
        setAi({ status: 'analyzing', suggestion: null, token: null, error: null });
        try {
            const result = file
                ? await adminApi.analyzeChallengeLoaderVideo(file)
                : await adminApi.analyzeSavedChallengeLoader(loader.id);
            setAi({ status: 'ready', suggestion: result.suggestion, token: result.analysisToken || null, error: null });
        } catch {
            // Never blocks anything: the admin just categorizes manually.
            setAi({ status: 'failed', suggestion: null, token: null, error: AI_FAILED });
        }
    };

    const applySuggestions = (draft) => {
        setForm((f) => ({
            ...f,
            name: draft.name.trim() || f.name,
            description: draft.description.trim(),
            categoryIds: draft.categoryIds,
            is_general: draft.isGeneral,
        }));
        setAi((a) => ({ ...a, status: 'applied', suggestion: null }));
    };
    const discardSuggestions = () => setAi({ status: 'idle', suggestion: null, token: null, error: null });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return setError('Name is required.');
        if (!isEdit && !file) return setError('Choose a video to upload.');
        if (form.available_from && form.available_until && form.available_from >= form.available_until) {
            return setError('“Available until” must be after “Available from”.');
        }
        setSaving(true);
        setError(null);
        try {
            const fields = {
                name: form.name.trim(),
                description: form.description.trim(),
                category_ids: form.categoryIds,
                is_general: form.is_general,
                is_active: form.is_active,
                weight: form.weight,
                ...(form.sort_order !== '' ? { sort_order: form.sort_order } : {}),
                available_from: form.available_from ? `${form.available_from}T00:00:00.000Z` : '',
                // End of the chosen day (UTC), so "until Oct 31" includes Oct 31.
                available_until: form.available_until ? `${form.available_until}T23:59:59.999Z` : '',
                // Only an applied suggestion is kept as the AI audit trail.
                ...(ai.status === 'applied' && ai.token ? { ai_analysis_token: ai.token } : {}),
            };
            const saved = isEdit
                ? await adminApi.updateChallengeLoader(loader.id, fields, file)
                : await adminApi.createChallengeLoader(fields, file);
            onSaved(saved, isEdit);
        } catch (err) {
            setError(err.message || 'Could not save the loader.');
        } finally {
            setSaving(false);
        }
    };

    const rec = meta.upload.recommended;
    return (
        <form className={styles.form} onSubmit={handleSubmit} aria-label={isEdit ? 'Edit loader' : 'New loader'}>
            <h2 className={styles.formTitle}>{isEdit ? `Edit “${loader.name}”` : 'New challenge loader'}</h2>

            <div className={styles.formGrid}>
                <div className={styles.previewCol}>
                    <div className={styles.previewBox}>
                        {preview ? (
                            <video src={preview} autoPlay muted loop playsInline controls={false} data-testid="loader-form-preview" />
                        ) : <span>No video</span>}
                    </div>
                    <label className={styles.fileBtn}>
                        {preview ? 'Replace video' : 'Choose video'} *
                        <input type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.m4v,.mov,.webm" onChange={handleFile} data-testid="loader-video-input" />
                    </label>
                    <small className={styles.hint}>
                        MP4 (H.264) preferred · square 1:1 · about {rec.minDimension}–{rec.maxDimension}px · {rec.minDurationSeconds}–{rec.maxDurationSeconds}s · no audio needed.
                        Other sizes and lengths are accepted with a warning. Audio is removed.
                        {isEdit && file && ' Saving replaces the video for everyone; categories and settings are kept.'}
                    </small>
                    {isEdit && (
                        <small className={styles.hint}>
                            {formatSeconds(loader.durationMs)} · {loader.width}×{loader.height} · {formatMb(loader.fileSizeBytes)}
                        </small>
                    )}
                </div>

                <div className={styles.fields}>
                    <div className={styles.aiBox}>
                        <button type="button" className={styles.secondaryBtn} onClick={analyze} disabled={!canAnalyze || ai.status === 'analyzing'}>
                            {ai.status === 'analyzing' ? 'Analyzing video…' : 'Suggest Categories with AI'}
                        </button>
                        <small className={styles.hint}>
                            {meta.ai.enabled
                                ? 'Optional. AI will inspect a few frames from this video and suggest a name and categories. You can edit everything before saving.'
                                : 'AI suggestions are not configured on this server. Categorize manually.'}
                        </small>
                        {ai.status === 'failed' && <div className={styles.warnBanner} role="status">{ai.error}</div>}
                        {ai.status === 'applied' && <small className={styles.hint}>Suggestions applied — review and Save.</small>}
                    </div>

                    {ai.status === 'ready' && ai.suggestion && (
                        <AiSuggestions suggestion={ai.suggestion} categories={categories} onApply={applySuggestions} onDiscard={discardSuggestions} />
                    )}

                    <label>Name *
                        <input value={form.name} onChange={set('name')} maxLength={80} placeholder="e.g. Motorcycle City Chase" aria-label="Name" />
                    </label>
                    <label>Description <span className={styles.optional}>(optional)</span>
                        <textarea rows={2} value={form.description} onChange={set('description')} maxLength={500} aria-label="Description" />
                    </label>

                    <fieldset className={styles.chipFieldset}>
                        <legend>Categories</legend>
                        <div className={styles.checkGrid}>
                            {categories.map((c) => (
                                <label key={c.id} className={styles.checkItem}>
                                    <input type="checkbox" checked={form.categoryIds.includes(c.id)} onChange={() => toggleCategory(c.id)} aria-label={c.name} />
                                    {c.name}
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    <label className={styles.checkboxRow}>
                        <input type="checkbox" checked={form.is_general} onChange={set('is_general')} aria-label="General fallback" />
                        General fallback — suits any title; used when nothing matches and mixed in for variety
                    </label>

                    <div className={styles.inlineFields}>
                        <label>Weight
                            <input type="number" min={meta.weight.min} max={meta.weight.max} step="1" value={form.weight} onChange={set('weight')} aria-label="Weight" />
                        </label>
                        <label>Sort order
                            <input type="number" min="0" step="1" value={form.sort_order} onChange={set('sort_order')} placeholder="Auto" aria-label="Sort order" />
                        </label>
                    </div>
                    <small className={styles.hint}>Higher weight = picked more often among eligible loaders.</small>

                    <label className={styles.checkboxRow}>
                        <input type="checkbox" checked={form.is_active} onChange={set('is_active')} aria-label="Active" />
                        Active (can be shown in challenges)
                    </label>

                    <fieldset className={styles.chipFieldset}>
                        <legend>Seasonal window <span className={styles.optional}>(optional — leave empty for always)</span></legend>
                        <div className={styles.inlineFields}>
                            <label>Available from
                                <input type="date" value={form.available_from} onChange={set('available_from')} aria-label="Available from" />
                            </label>
                            <label>Available until
                                <input type="date" value={form.available_until} onChange={set('available_until')} aria-label="Available until" />
                            </label>
                        </div>
                    </fieldset>
                </div>
            </div>

            {error && <div className={styles.errorBanner} role="alert">{error}</div>}

            <div className={styles.formActions}>
                <button type="button" className={styles.secondaryBtn} onClick={onCancel} disabled={saving}>Cancel</button>
                <button type="submit" className={styles.primaryBtn} disabled={saving}>
                    {saving ? (file ? 'Uploading & processing…' : 'Saving…') : isEdit ? 'Save changes' : 'Save loader'}
                </button>
            </div>
        </form>
    );
}

// ── Categories ───────────────────────────────────────────────────────────────

function CategoryManager({ categories, onChanged, onClose }) {
    const [newName, setNewName] = useState('');
    const [drafts, setDrafts] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const draft = (c) => drafts[c.id] ?? { name: c.name, sortOrder: String(c.sortOrder) };
    const setDraft = (c, patch) => setDrafts((d) => ({ ...d, [c.id]: { ...draft(c), ...patch } }));
    const run = async (fn) => {
        setBusy(true);
        setError(null);
        try {
            await fn();
            await onChanged();
        } catch (err) {
            setError(err.message || 'Something went wrong.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className={styles.form} aria-label="Loader categories">
            <div className={styles.categoryHeader}>
                <h2 className={styles.formTitle}>Categories</h2>
                <button type="button" className={styles.secondaryBtn} onClick={onClose}>Done</button>
            </div>
            <p className={styles.hint}>
                Standard genres match titles automatically from TMDB (Anime = Japanese animation, Superhero = TMDB “superhero” keyword).
                Custom categories can tag loaders but only match titles once mapped in code. General is the per-loader fallback toggle.
            </p>
            {error && <div className={styles.errorBanner} role="alert">{error}</div>}
            <table className={styles.table}>
                <thead><tr><th>Name</th><th>Key</th><th>Sort order</th><th>Loaders</th><th>Actions</th></tr></thead>
                <tbody>
                    {categories.map((c) => {
                        const d = draft(c);
                        const dirty = d.name !== c.name || d.sortOrder !== String(c.sortOrder);
                        return (
                            <tr key={c.id}>
                                <td><input className={styles.inlineInput} value={d.name} maxLength={60} onChange={(e) => setDraft(c, { name: e.target.value })} aria-label={`Name of ${c.name}`} /></td>
                                <td><code>{c.slug}</code></td>
                                <td><input className={styles.inlineInput} type="number" min="0" value={d.sortOrder} onChange={(e) => setDraft(c, { sortOrder: e.target.value })} aria-label={`Sort order of ${c.name}`} /></td>
                                <td>{c.loaderCount}</td>
                                <td className={styles.rowActions}>
                                    <button type="button" disabled={busy || !dirty} onClick={() => run(async () => {
                                        await adminApi.updateChallengeLoaderCategory(c.id, { name: d.name.trim(), sort_order: d.sortOrder });
                                        setDrafts((all) => { const next = { ...all }; delete next[c.id]; return next; });
                                    })}>Save</button>
                                    <button type="button" className={styles.dangerLink} disabled={busy || c.loaderCount > 0}
                                        title={c.loaderCount > 0 ? 'Remove it from its loaders first' : 'Delete category'}
                                        onClick={() => { if (window.confirm(`Delete the “${c.name}” category?`)) run(() => adminApi.deleteChallengeLoaderCategory(c.id)); }}>
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <form className={styles.addCategory} onSubmit={(e) => {
                e.preventDefault();
                if (!newName.trim()) return;
                run(async () => { await adminApi.createChallengeLoaderCategory({ name: newName.trim() }); setNewName(''); });
            }}>
                <input className={styles.inlineInput} value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={60}
                    placeholder="New category, e.g. Classic Cinema" aria-label="New category name" />
                <button type="submit" className={styles.primaryBtn} disabled={busy || !newName.trim()}>Add category</button>
            </form>
        </section>
    );
}

// ── Preview + delete dialogs ─────────────────────────────────────────────────

function PreviewDialog({ loader, onClose }) {
    return (
        <Modal isOpen onClose={onClose}>
            <div className={styles.dialog} aria-label={`Preview ${loader.name}`}>
                <h3>{loader.name}</h3>
                <div className={styles.previewLarge}>
                    <video src={loader.videoUrl} poster={loader.thumbnailUrl || undefined} autoPlay muted loop playsInline controls={false} data-testid="loader-preview-video" />
                </div>
                {loader.description && <p>{loader.description}</p>}
                <p className={styles.hint}>
                    {formatSeconds(loader.durationMs)} · {loader.width}×{loader.height} · {formatMb(loader.fileSizeBytes)} · shown {loader.selectionCount} times
                </p>
            </div>
        </Modal>
    );
}

function DeleteDialog({ loader, onCancel, onDeleted }) {
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);
    const handleDelete = async () => {
        setDeleting(true);
        setError(null);
        try {
            await adminApi.deleteChallengeLoader(loader.id);
            onDeleted(loader);
        } catch (err) {
            setError(err.message || 'Could not delete the loader.');
            setDeleting(false);
        }
    };
    return (
        <Modal isOpen onClose={deleting ? () => {} : onCancel}>
            <div className={styles.dialog} role="alertdialog" aria-labelledby="delete-loader-title">
                <h3 id="delete-loader-title">Delete loader?</h3>
                <p>“{loader.name}” and its video will be removed permanently. It stops being chosen for new challenges right away.</p>
                {loader.isActive && <p className={styles.hint}>To retire it but keep the video, disable it instead.</p>}
                {error && <div className={styles.errorBanner} role="alert">{error}</div>}
                <div className={styles.formActions}>
                    <button type="button" className={styles.secondaryBtn} onClick={onCancel} disabled={deleting}>Cancel</button>
                    <button type="button" className={styles.dangerBtn} onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Deleting…' : 'Delete loader'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function AdminChallengeLoaders() {
    const [loaders, setLoaders] = useState([]);
    const [categories, setCategories] = useState([]);
    const [meta, setMeta] = useState(FALLBACK_META);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [filter, setFilter] = useState('ALL');
    const [editing, setEditing] = useState(null); // null | 'new' | loader
    const [previewing, setPreviewing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [managingCategories, setManagingCategories] = useState(false);
    const topRef = useRef(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const data = await adminApi.listChallengeLoaders();
            setLoaders(data.loaders || []);
            setCategories(data.categories || []);
            setMeta({ ...FALLBACK_META, ...data, upload: { ...FALLBACK_META.upload, ...(data.upload || {}) }, ai: { ...FALLBACK_META.ai, ...(data.ai || {}) } });
        } catch (err) {
            setError(err.message || 'Failed to load challenge loaders.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const startEditing = (value) => {
        setMessage(null);
        setWarnings([]);
        setEditing(value);
        topRef.current?.scrollIntoView?.({ behavior: 'smooth' });
    };

    const handleSaved = (saved, isEdit) => {
        setEditing(null);
        setMessage(isEdit ? `Saved “${saved.name}”.` : `Uploaded “${saved.name}”.`);
        setWarnings(saved.warnings || []);
        load();
    };

    const toggleActive = async (loader) => {
        setError(null);
        try {
            const saved = await adminApi.updateChallengeLoader(loader.id, { is_active: !loader.isActive });
            setLoaders((list) => list.map((l) => (l.id === loader.id ? { ...l, ...saved } : l)));
        } catch (err) {
            setError(err.message || 'Could not update the loader.');
        }
    };

    const filters = useMemo(() => [
        { value: 'ALL', label: `All (${loaders.length})` },
        { value: 'GENERAL', label: `General (${loaders.filter((l) => l.isGeneral).length})` },
        { value: 'INACTIVE', label: `Inactive (${loaders.filter((l) => !l.isActive).length})` },
        ...categories.filter((c) => c.loaderCount > 0).map((c) => ({ value: c.slug, label: `${c.name} (${c.loaderCount})` })),
    ], [loaders, categories]);

    const visible = loaders.filter((l) => {
        if (filter === 'ALL') return true;
        if (filter === 'GENERAL') return l.isGeneral;
        if (filter === 'INACTIVE') return !l.isActive;
        return l.categories.some((c) => c.slug === filter);
    });
    const activeGeneral = loaders.filter((l) => l.isGeneral && l.isActive).length;

    return (
        <div className={styles.page} ref={topRef}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.pageTitle}>⏳ Challenge Loaders</h1>
                    <p className={styles.subtitle}>
                        Short looping animations shown while a challenge is prepared, matched to the title’s genres.
                        General loaders are the fallback and are mixed in about {Math.round(meta.selection.generalShare * 100)}% of the time.
                    </p>
                </div>
                {editing === null && (
                    <div className={styles.headerActions}>
                        <button type="button" className={styles.secondaryBtn} onClick={() => setManagingCategories((v) => !v)}>Manage categories</button>
                        <button type="button" className={styles.primaryBtn} onClick={() => startEditing('new')}>+ Upload loader</button>
                    </div>
                )}
            </div>

            {!loading && activeGeneral === 0 && (
                <div className={styles.warnBanner} role="status">
                    No active General loaders. Titles without a genre match will use the standard spinner.
                </div>
            )}
            {message && <div className={styles.message}>{message}</div>}
            {warnings.length > 0 && (
                <ul className={styles.warnList} aria-label="Upload warnings">
                    {warnings.map((w) => <li key={w.code}>⚠️ {w.message}</li>)}
                </ul>
            )}
            {error && <div className={styles.errorBanner} role="alert">{error}</div>}

            {managingCategories && editing === null && (
                <CategoryManager categories={categories} onChanged={load} onClose={() => setManagingCategories(false)} />
            )}

            {editing !== null && (
                <LoaderForm
                    key={editing === 'new' ? 'new' : editing.id}
                    loader={editing === 'new' ? null : editing}
                    categories={categories}
                    meta={meta}
                    onCancel={() => setEditing(null)}
                    onSaved={handleSaved}
                />
            )}

            <div className={styles.filters} role="tablist" aria-label="Filter loaders">
                {filters.map((f) => (
                    <button key={f.value} type="button" role="tab" aria-selected={filter === f.value}
                        className={`${styles.filterPill} ${filter === f.value ? styles.filterPillActive : ''}`}
                        onClick={() => setFilter(f.value)}>
                        {f.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className={styles.empty}>Loading loaders…</div>
            ) : visible.length === 0 ? (
                <div className={styles.empty}>No loaders here yet. Upload one to start the library.</div>
            ) : (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Thumbnail</th>
                                <th>Name</th>
                                <th>Categories</th>
                                <th>General</th>
                                <th>Active</th>
                                <th>Weight</th>
                                <th>Duration</th>
                                <th>Times shown</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((l) => (
                                <tr key={l.id} className={l.isActive ? '' : styles.inactiveRow}>
                                    <td>
                                        <button type="button" className={styles.thumbBtn} onClick={() => setPreviewing(l)} aria-label={`Preview ${l.name}`}>
                                            {l.thumbnailUrl ? <img className={styles.thumb} src={l.thumbnailUrl} alt="" /> : <span className={styles.thumbEmpty}>▶</span>}
                                        </button>
                                    </td>
                                    <td>
                                        <div className={styles.loaderName}>{l.name}</div>
                                        {l.seasonalStatus !== 'always' && <span className={styles.badge}>{l.seasonalStatus === 'live' ? 'Seasonal · live' : `Seasonal · ${l.seasonalStatus}`}</span>}
                                        {!l.selectable && <div className={styles.warnText}>Never shown — add a category or mark General</div>}
                                    </td>
                                    <td>
                                        <div className={styles.chips}>
                                            {l.categories.length ? l.categories.map((c) => <span key={c.id} className={styles.chip}>{c.name}</span>) : <span className={styles.hint}>—</span>}
                                        </div>
                                    </td>
                                    <td>{l.isGeneral ? <span className={styles.badge}>General</span> : '—'}</td>
                                    <td>
                                        <button type="button" className={`${styles.statusPill} ${l.isActive ? styles.statusOn : styles.statusOff}`}
                                            onClick={() => toggleActive(l)} title={l.isActive ? 'Disable (stop showing it)' : 'Enable'}>
                                            {l.isActive ? 'Active' : 'Inactive'}
                                        </button>
                                    </td>
                                    <td>{l.weight}</td>
                                    <td>{formatSeconds(l.durationMs)}</td>
                                    <td>{l.selectionCount}</td>
                                    <td className={styles.rowActions}>
                                        <button type="button" onClick={() => startEditing(l)}>Edit</button>
                                        <button type="button" className={styles.dangerLink} onClick={() => setDeleting(l)}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {previewing && <PreviewDialog loader={previewing} onClose={() => setPreviewing(null)} />}
            {deleting && (
                <DeleteDialog
                    loader={deleting}
                    onCancel={() => setDeleting(null)}
                    onDeleted={(l) => { setDeleting(null); setMessage(`Deleted “${l.name}”.`); setLoaders((list) => list.filter((x) => x.id !== l.id)); load(); }}
                />
            )}
        </div>
    );
}
